import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { DocumentTextIcon, FileIcon } from '../../../../components/ui/Icons';
import { API_ENDPOINTS } from '../../../../config/apiConfig';
import { normalizeFileName } from '../../../../components/ui/utils/fileNameEncoding';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent, 
  Badge 
} from '../../../../components/ui';

const ApprovalDetails = ({ selectedRecord }) => {
  const { theme } = useTheme();

  return (
    <>
      {/* Description */}
      {selectedRecord.description && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center">
              <DocumentTextIcon className="w-4 h-4 mr-2" />
              Description
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className={cn("text-sm leading-relaxed", theme.text.primary)}>
              {selectedRecord.description}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Entity Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center">
            <DocumentTextIcon className="w-4 h-4 mr-2" />
            Entity Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b">
              <span className={cn("text-sm font-medium", theme.text.secondary)}>Type</span>
              <Badge variant="outline">
                {selectedRecord.entityType?.name?.charAt(0).toUpperCase() + selectedRecord.entityType?.name?.slice(1)}
              </Badge>
            </div>
            {selectedRecord.entityData && (
              <div className="space-y-2">
                {selectedRecord.entityData.documentCode && (
                  <div className="flex items-center justify-between py-2">
                    <span className={cn("text-sm", theme.text.secondary)}>Code</span>
                    <span className={cn("text-sm font-medium font-mono", theme.text.primary)}>
                      {selectedRecord.entityData.documentCode}
                    </span>
                  </div>
                )}
                {selectedRecord.entityData.documentTitle && (
                  <div className="flex items-center justify-between py-2">
                    <span className={cn("text-sm", theme.text.secondary)}>Title</span>
                    <span className={cn("text-sm font-medium", theme.text.primary)}>
                      {selectedRecord.entityData.documentTitle}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* File Attachments (for entityTypeId 1 or 2) */}
      {selectedRecord.fileAttachments && 
       Array.isArray(selectedRecord.fileAttachments) && 
       selectedRecord.fileAttachments.length > 0 && 
       (selectedRecord.entityTypeId === 1 || selectedRecord.entityTypeId === 2) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center">
              <FileIcon className="w-4 h-4 mr-2" />
              File Attachments ({selectedRecord.fileAttachments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {selectedRecord.fileAttachments.map((file, index) => (
                <div 
                  key={index} 
                  className={cn(
                    "flex items-center gap-3 p-2 rounded-lg border",
                    theme.colors.background,
                    theme.border.subtle
                  )}
                >
                  <FileIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className={cn("text-sm font-medium truncate", theme.text.primary)}>
                      {normalizeFileName(file.fileName || '')}
                    </div>
                    <div className={cn("text-xs", theme.text.secondary)}>
                      {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(2)} MB` : 'Unknown size'} • {file.fileType || 'Unknown type'}
                    </div>
                  </div>
                  <a 
                    href={API_ENDPOINTS.fileAttachment.download(file.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      "text-xs px-3 py-1 rounded-md transition-colors",
                      theme.colors.primary,
                      theme.text.inverse,
                      "hover:opacity-80"
                    )}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Download
                  </a>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};

export default ApprovalDetails;
